'use client';

import { useEffect, useRef } from 'react';

import { motion, useAnimation, useInView } from 'framer-motion';

import NumberTicker from '@/components/ui/number-ticker';
import { ny } from '@/lib/utils';

type TAnimatedInfoLineProps = {
    title?: number;
    prefixTitle?: string;
    postfixTitle?: string;
    text?: string;

    classnames?: {
        root?: string;
        info?: string;
        postfixTitle?: string;
        title?: string;
        text?: string;
        underline?: string;
    };
};

export function AnimatedInfoLine({
    text = '',
    title = 0,
    prefixTitle = '',
    classnames = {},
    postfixTitle,
}: TAnimatedInfoLineProps) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-50px 0px' }); // Триггер, когда компонент попадает в зону видимости

    const underlineControls = useAnimation();
    const textControls = useAnimation();

    useEffect(() => {
        if (isInView) {
            async function runAnimation() {
                await underlineControls.start({
                    scaleX: 1,
                    transition: { delay: 0.2, duration: 0.5, ease: 'easeOut' },
                });
                await textControls.start({
                    filter: 'blur(0px)',
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.3, duration: 0.8 },
                });
            }
            runAnimation();
        }
    }, [isInView, underlineControls, textControls]);

    return (
        <div ref={ref} className={ny('info-list jstify-center flex flex-col gap-3', classnames.root)}>
            <div
                className={ny(
                    'info-list--row relative flex flex-col justify-between p-4 md:flex-row md:items-center',
                    classnames.info
                )}
            >
                <div className={'flex w-1/2 flex-nowrap items-center gap-2'}>
                    <span className={ny('whitespace-nowrap text-primary-foreground', classnames.title)}>
                        {prefixTitle}
                    </span>
                    <NumberTicker
                        delay={0.01}
                        direction={title <= 100 ? 'up' : 'down'}
                        textContent={title}
                        className={ny('whitespace-nowrap text-primary-foreground', classnames.title)}
                    />
                    <span className={ny('whitespace-nowrap text-primary-foreground', classnames.title)}>
                        {postfixTitle}
                    </span>
                </div>
                <div className="flex w-full items-center md:w-1/2 md:justify-center">
                    <motion.h3
                        className={ny('flex w-[90%] text-primary-foreground md:w-[80%] lg:w-[55%]', classnames.text)}
                        style={{ lineHeight: '1.75rem' }}
                        initial={{ filter: 'blur(10px)', opacity: 0, y: 10 }}
                        animate={textControls}
                    >
                        {text}
                    </motion.h3>
                </div>

                <motion.div
                    className={ny(
                        'info-list--row-underline absolute bottom-0 left-0 right-0 h-1 bg-primary-foreground',
                        classnames.underline
                    )}
                    initial={{ scaleX: 0 }}
                    animate={underlineControls}
                    style={{ originX: 0 }}
                />
            </div>
        </div>
    );
}
